import { IPost } from '_interfaces/post.interface';
import { IPostType } from '_interfaces/systemPost.types';
import { IPostStatus } from '_interfaces/types/post.types';
import { filteredPostCommentsAtom } from 'atoms/postComments';
import { commentMatchesSearch, postMatchesSearch } from 'atoms/utils/searchUtils';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { deleteMediaEntriesByPost } from 'utils/services/fileCabinetService';
import { atom } from 'jotai';
import { atomFamily, atomWithObservable, atomWithStorage, loadable } from 'jotai/utils';
import { Observable } from 'rxjs';
import { auth, db, refreshFirebaseToken } from 'utils/firebase';
import { selectedCompanyIdAtom } from '../company';
import { selectedProjectAtom } from '../projects';
import { createCrudAtoms } from '../utils/crudFactory';

export const scheduleFeedSearchAtom = atom<string>('');

// Persistent cache for posts data
export const scheduleFeedPostsCacheAtom = atomWithStorage<IPost[]>('scheduleFeedPostsCache', []);
export const localScheduleFeedDraftPostsAtom = atomWithStorage<Partial<IPost>[]>(
  'localScheduleFeedDraftPosts',
  []
);

// Real-time Firestore listener for project feed using atomFamily
export const scheduleFeedPostsAtom = atomFamily((scheduleItemId: string) =>
  atomWithObservable<IPost[]>(
    (get) => {
      const project = get(selectedProjectAtom);
      const companyId = get(selectedCompanyIdAtom);

      // Use cached posts synchronously as an immediate first value
      const cached = get(scheduleFeedPostsCacheAtom);
      const initial: IPost[] = cached instanceof Promise ? [] : cached;

      if (!project?.id) {
        return new Observable<IPost[]>((subscriber) => {
          // Emit synchronously to avoid suspension
          subscriber.next(initial);
        });
      }

      const postQuery = query(
        collection(db, `companies/${companyId}/projects/${project.id}/scheduleFeed`),
        where('itemId', '==', scheduleItemId),
        orderBy('createdAt', 'desc')
      );

      return new Observable<IPost[]>((subscriber) => {
        // Emit synchronously so reads never suspend on first access
        subscriber.next(initial);

        const unsubscribe = onSnapshot(postQuery, (snapshot) => {
          const postData: IPost[] = snapshot.docs.map((doc) =>
            IPost.fromDocumentData({ ...doc.data(), id: doc.id })
          );
          subscriber.next(postData);
        });

        return () => {
          unsubscribe();
        };
      });
    },
    {
      // Fallback initial value in case cached is a Promise
      initialValue: [],
    }
  )
);

export const filteredScheduleFeedPostsAtom = atomFamily(
  ({ scheduleItemId, searchQuery }: { scheduleItemId: string; searchQuery: string }) =>
    atom((get) => {
      const allPosts = get(scheduleFeedPostsAtom(scheduleItemId));
      const posts = allPosts.filter((p: IPost) => p.status !== IPostStatus.DRAFT);

      if (!searchQuery.trim()) {
        return posts;
      }

      return posts.filter((post: IPost) => {
        if (postMatchesSearch(post, searchQuery)) {
          return true;
        }

        const comments = get(
          filteredPostCommentsAtom({ parentPath: `${post.parentPath}/${post.id}`, searchQuery })
        );
        return comments.some((comment) => commentMatchesSearch(comment, searchQuery));
      });
    })
);

// Sync latest Firestore data into cache every time postsAtom updates
export const scheduleFeedPostsSyncAtom = atomFamily((scheduleItemId: string) =>
  atom(null, async (get, set) => {
    const loaded = get(loadable(scheduleFeedPostsAtom(scheduleItemId)));
    if (loaded.state === 'hasData') {
      set(scheduleFeedPostsCacheAtom, loaded.data);
    }
    // If loading or hasError, do nothing – avoid triggering Suspense from a write
  })
);

export const addScheduleFeedPostAtom = atomFamily((scheduleItemId: string) =>
  atom(
    null,
    async (
      get,
      set,
      params: {
        newPost: Partial<IPost>;
        user: { displayName: string; avatarUrl: string; userId: string; role: string };
      }
    ) => {
      // Ensure Firebase authentication is valid
      let firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        const refreshSuccess = await refreshFirebaseToken();
        if (!refreshSuccess) {
          throw new Error('Failed to authenticate with Firebase');
        }
        firebaseUser = auth.currentUser;
      }

      const { newPost, user } = params;
      const project = get(selectedProjectAtom)!;
      const companyId = get(selectedCompanyIdAtom);

      const parentPath = `companies/${companyId}/projects/${project.id}/scheduleFeed`;

      let postDocRef;
      if (newPost.id) {
        postDocRef = doc(db, `${parentPath}/${newPost.id}`);
      } else {
        postDocRef = doc(collection(db, parentPath));
      }

      const draftPost: Partial<IPost> = {
        id: postDocRef.id,
        projectId: project.id.toString(),
        companyId,
        isDeleted: false,
        isEdited: false,
        itemId: scheduleItemId,
        replyCount: 0,
        createdAt: new Date(),
        message: newPost.message || '',
        mentions: newPost.mentions || [],
        hashtags: newPost.hashtags || [],
        tags: newPost.tags || [],
        phaseIds: newPost.phaseIds || [],
        phaseItemIds: newPost.phaseItemIds || [],
        // visibility: newPost.visibility || 'public',
        likes: [],
        media: newPost.media || [],
        status: IPostStatus.DRAFT,
        postType: IPostType.USER_POST,
        createdBy: {
          userId: user?.userId || '',
          displayName: user?.displayName || '',
          avatarUrl: user?.avatarUrl || '',
          role: user?.role || '',
        },
        parentPath,
        // isLocalDraft: true,
      };

      // Add to local drafts immediately
      set(localScheduleFeedDraftPostsAtom, (drafts) => [draftPost, ...drafts]);

      try {
        const finalPost = {
          ...draftPost,
          status: IPostStatus.ACTIVE,
          id: postDocRef.id,
          createdAt: serverTimestamp() as any,
          companyId: companyId,
          projectId: project.id.toString(),
          itemId: scheduleItemId,
          isDeleted: false,
          isEdited: false,
          // isLocalDraft: false,
        } as IPost;

        if (finalPost.message !== '<p></p>\n' || finalPost.media.length > 0) {
          await setDoc(postDocRef, finalPost);
          // Remove draft + rely on real-time Firestore update to insert final post
          set(localScheduleFeedDraftPostsAtom, (drafts) =>
            drafts.filter((d) => d.id !== postDocRef.id)
          );
        }
      } catch (error) {
        console.error('Error creating post:', error);
        set(localScheduleFeedDraftPostsAtom, (drafts) =>
          drafts.filter((d) => d.id !== postDocRef.id)
        );
        throw error;
      }
    }
  )
);

export const updateScheduleFeedPostAtom = atom(null, async (get, set, post: Partial<IPost>) => {
  const project = get(selectedProjectAtom)!;
  const companyId = get(selectedCompanyIdAtom);

  const { updateAtom } = createCrudAtoms<IPost>(
    `companies/${companyId}/projects/${project.id}/scheduleFeed`
  );
  await set(updateAtom, { id: post.id, data: post });
});

export const deleteScheduleFeedPostAtom = atom(null, async (get, set, id: string) => {
  const project = get(selectedProjectAtom)!;
  const companyId = get(selectedCompanyIdAtom);
  if (!project?.id || !companyId) return;

  // Step 1: Delete Firestore post doc — UI updates instantly via listener
  const { deleteAtom } = createCrudAtoms<IPost>(
    `companies/${companyId}/projects/${project.id}/scheduleFeed`
  );
  await set(deleteAtom, id);

  // Step 2: Background — delete comments subcollection + FC records (fire-and-forget)
  void (async () => {
    try {
      const commentsPath = `companies/${companyId}/projects/${project.id}/scheduleFeed/${id}/comments`;
      await Promise.allSettled([
        (async () => {
          const commentsSnap = await getDocs(collection(db, commentsPath));
          await Promise.all(commentsSnap.docs.map((commentDoc) => deleteDoc(commentDoc.ref)));
        })(),
        deleteMediaEntriesByPost(project.id, id),
      ]);
    } catch (err) {
      console.error('[ScheduleFeed] Background delete cleanup failed:', err);
    }
  })();
});

export const toggleScheduleFeedPostLikeAtom = atom(
  null,
  async (
    get,
    set,
    payload: {
      scheduleItemId: string;
      userId: string;
      postId: string;
    }
  ) => {
    // Check Firebase auth state first
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('No Firebase user authenticated');
      throw new Error('User must be authenticated to like posts');
    }

    const { userId, postId, scheduleItemId } = payload;

    if (!postId || !scheduleItemId || !userId) {
      console.error('Post ID, User ID or Schedule Item ID missing:', {
        postId,
        scheduleItemId,
        userId,
      });
      throw new Error('Post ID, User ID or Schedule Item ID missing');
    }

    // Check what's in the cache
    const allPosts = get(scheduleFeedPostsAtom(scheduleItemId));

    const post = allPosts.find((p) => p.id === postId);

    if (!post) {
      console.error('Post not found with ID:', postId);
      console.error('Error getting post from Firestore:', postId);
      return;
    }

    const hasLiked = post.likes?.includes(userId) ?? false;
    const newlikes = hasLiked
      ? post.likes?.filter((uid) => uid !== userId)
      : [...(post.likes || []), userId];

    // Use direct Firestore update instead of createCrudAtoms
    try {
      await set(updateScheduleFeedPostAtom, { ...post, likes: newlikes });

      // Update the cache to reflect the change
      set(scheduleFeedPostsCacheAtom, (current) =>
        current.map((p) => (p.id === postId ? ({ ...p, likes: newlikes } as IPost) : p))
      );
    } catch (error) {
      console.error('Error updating Firestore:', error);
      throw error;
    }
  }
);
